(() => {
  const assembleAscii = (root, art, frame, onComplete) => {
    const stage = root.querySelector('[data-vlm-ascii-stage]');
    const particlesLayer = root.querySelector('[data-vlm-ascii-particles]');
    const intro = root.querySelector('[data-vlm-intro]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    art.textContent = frame;

    if (!stage || !particlesLayer || !intro || reducedMotion) {
      art.classList.add('is-assembled');
      onComplete();
      return;
    }

    window.requestAnimationFrame(() => {
      const lines = frame.split('\n');
      const candidates = [];

      lines.forEach((line, row) => {
        Array.from(line).forEach((character, column) => {
          if (character !== ' ') candidates.push({ character, column, row });
        });
      });

      const maximumParticles = 170;
      const sampleStep = Math.max(1, Math.ceil(candidates.length / maximumParticles));
      const particles = candidates.filter((_, index) => index % sampleStep === 0).slice(0, maximumParticles);
      const artRect = art.getBoundingClientRect();
      const stageRect = stage.getBoundingClientRect();
      const introRect = intro.getBoundingClientRect();
      const cellWidth = artRect.width / 84;
      const cellHeight = artRect.height / 42;
      const stageX = stageRect.left - introRect.left;
      const stageY = stageRect.top - introRect.top;

      particlesLayer.replaceChildren();

      particles.forEach(({ character, column, row }, index) => {
        const particle = document.createElement('span');
        const targetX = column * cellWidth;
        const targetY = row * cellHeight;
        const edge = index % 4;
        let startX = Math.random() * introRect.width - stageX;
        let startY = Math.random() * introRect.height - stageY;

        if (edge === 0) startX = -stageX - 30;
        if (edge === 1) startX = introRect.width - stageX + 30;
        if (edge === 2) startY = -stageY - 30;
        if (edge === 3) startY = introRect.height - stageY + 30;

        particle.className = 'vlm-ascii-particle';
        particle.textContent = character;
        particle.style.left = `${targetX}px`;
        particle.style.top = `${targetY}px`;
        particlesLayer.appendChild(particle);

        particle.animate(
          [
            {
              opacity: 0.08,
              transform: `translate3d(${startX - targetX}px, ${startY - targetY}px, 0) rotate(${(index % 9 - 4) * 18}deg) scale(${3.5 + (index % 4) * 0.65})`,
            },
            { opacity: 1, offset: 0.16 },
            { opacity: 1, transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)' },
          ],
          {
            duration: 1200 + Math.random() * 500,
            delay: Math.random() * 260,
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            fill: 'forwards',
          }
        );
      });

      root.vlmAsciiAssemblyTimer = window.setTimeout(() => {
        art.classList.add('is-assembled');
        particlesLayer.classList.add('is-finished');
        onComplete();

        window.setTimeout(() => particlesLayer.replaceChildren(), 500);
      }, 1850);
    });
  };

  const initializeAscii = (root) => {
    const art = root.querySelector('[data-vlm-ascii]');
    const source = root.dataset.asciiUrl;

    if (!art || !source) return;

    fetch(source)
      .then((response) => {
        if (!response.ok) throw new Error('ASCII frames could not be loaded.');
        return response.text();
      })
      .then((text) => {
        const frames = text
          .split(/^--- frame \d+\/\d+ ---\r?$/m)
          .slice(1)
          .map((frame) => frame.replace(/^\r?\n/, '').replace(/\r/g, '').replace(/\n$/, ''))
          .filter(Boolean);

        if (!frames.length || root.classList.contains('is-entering')) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let frameIndex = Math.min(80, frames.length - 1);

        assembleAscii(root, art, frames[frameIndex], () => {
          if (reducedMotion || root.classList.contains('is-entering')) return;

          root.vlmAsciiTimer = window.setInterval(() => {
            frameIndex = (frameIndex + 1) % frames.length;
            art.textContent = frames[frameIndex];
          }, 30);
        });
      })
      .catch(() => {
        art.textContent = '       *\n    *  *  *\n  *    *    *\n    *  *  *\n       *';
        art.classList.add('is-assembled');
      });
  };

  const fillLoopColumns = (root) => {
    root.querySelectorAll('.vlm-loop-column').forEach((column) => {
      if (column.offsetHeight === 0) return;

      const sets = column.querySelectorAll('.vlm-loop-set');
      const sourceWord = sets[0]?.querySelector('.vlm-loop-word');
      const track = column.querySelector('.vlm-loop-track');

      if (sets.length !== 2 || !sourceWord || !track) return;

      const largestScreenSide = Math.max(window.screen?.width || 0, window.screen?.height || 0);
      const requiredHeight = Math.max(column.clientHeight * 1.35, largestScreenSide * 1.4);
      let addedWords = 0;

      while (sets[0].scrollHeight < requiredHeight && addedWords < 100) {
        sets.forEach((set) => set.appendChild(sourceWord.cloneNode(true)));
        addedWords += 1;
      }

      column.classList.remove('is-loop-ready');
      track.style.setProperty('--vlm-loop-offset', `${sets[0].getBoundingClientRect().height * -1}px`);
      void track.offsetHeight;
      column.classList.add('is-loop-ready');
    });
  };

  const initialize = (root) => {
    if (!root || root.dataset.vlmInitialized === 'true') return;

    root.dataset.vlmInitialized = 'true';
    const delay = Number(root.dataset.revealDelay || 1500);
    const enterButton = root.querySelector('[data-vlm-enter]');
    const accessToggle = root.querySelector('[data-vlm-access-toggle]');
    const accessForm = root.querySelector('[data-vlm-access-form]');

    const setAccessOpen = (open) => {
      if (!accessToggle || !accessForm) return;

      accessForm.hidden = !open;
      accessToggle.setAttribute('aria-expanded', String(open));
      accessToggle.textContent = open ? 'CLOSE PRIVATE ACCESS' : 'PRIVATE ACCESS';
    };

    const revealPassword = (immediate = false) => {
      if (root.classList.contains('is-stage-visible')) return;

      window.clearInterval(root.vlmAsciiTimer);
      window.clearTimeout(root.vlmAsciiAssemblyTimer);
      root.classList.add('is-stage-visible');

      if (immediate) {
        root.classList.add('is-entering', 'is-intro-hidden', 'is-ready');
        return;
      }

      window.requestAnimationFrame(() => root.classList.add('is-entering'));
      window.setTimeout(() => root.classList.add('is-intro-hidden'), 650);
      window.setTimeout(() => root.classList.add('is-ready'), Math.max(delay, 1000));
    };

    fillLoopColumns(root);
    initializeAscii(root);

    accessToggle?.addEventListener('click', () => {
      setAccessOpen(accessToggle.getAttribute('aria-expanded') !== 'true');
    });

    if (root.querySelector('.vlm-password-error')) {
      setAccessOpen(true);
      revealPassword(true);
    } else if (root.querySelector('.vlm-form-error, .vlm-newsletter-success')) {
      revealPassword(true);
    } else {
      enterButton?.addEventListener('click', () => revealPassword(false));
    }

    let resizeFrame;
    let lastViewportWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      if (Math.abs(window.innerWidth - lastViewportWidth) < 2) return;

      lastViewportWidth = window.innerWidth;
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => fillLoopColumns(root));
    });

  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-vlm-password]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
